import { useEffect, useRef, useState } from "react";
import { AxiosResponse } from "axios";
import liff from "@line/liff";
import { Text, XStack, YStack } from "tamagui";
import api, { handleRequest } from "@services/axios/apiManager";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { getSendOtpErrorMessage } from "@utils/otpError";
import { VOICE_OTP_CALLER_ID } from "@utils/func/voiceOtp";
import { kiosk } from "../kioskTheme";
import { useKiosk } from "../useKiosk";
import { OTP_RESEND_SECS } from "../otpPrefetch";
import { Card, SectionLabel } from "./ConfirmParts";

/**
 * 結算頁內嵌的簡訊 OTP 驗證區塊（首次結帳時顯示於確認頁，非彈窗）。
 * 掛載時向後端發送簡訊 OTP；填滿 5 碼向後端驗證，成功後把 phoneVerifiedTicket
 * 存進 useKiosk 並標記 otpVerified=true — confirm.tsx 的送出按鈕以 otpVerified 為準，
 * 不再前端接受任意 5 碼。
 */
const OtpCard = ({
  code,
  setCode,
  phone,
  alreadyVerified,
}: {
  code: string[];
  setCode: (v: string[]) => void;
  phone: string;
  /** 進頁時 otpVerified 已為 true（本 session 驗過）→ 直接顯示「驗證成功」、不重送 OTP。 */
  alreadyVerified?: boolean;
}) => {
  const { lineAccessToken } = useLineAuth();
  const { setOtpVerified, setPhoneVerifiedTicket, setOtpSent } = useKiosk();
  /**
   * 「最近是否已寄給同一支門號」——一律用 getState() 現讀，不可用 render 時解構的快照。
   * 預寄是非同步的：OtpCard 很可能在購物車那次 setOtpSent 之前就已 render，
   * closure 裡會是空值 → 防重寄與 429 靜默兩道防護同時失效（照發、又報紅字）。
   */
  const recentlySentTo = (target: string) => {
    const digits = (target || "").replace(/\D/g, "");
    if (!digits) return 0;
    const { otpSentPhone, otpSentAt } = useKiosk.getState();
    if (otpSentPhone !== digits) return 0;
    const left = OTP_RESEND_SECS - Math.floor((Date.now() - otpSentAt) / 1000);
    return left > 0 ? left : 0;
  };
  const [secs, setSecs] = useState(OTP_RESEND_SECS);
  // 語音冷卻與簡訊分開（後端兩個管道各自計冷卻）：簡訊冷卻中仍要能立刻改用語音。
  const [voiceSecs, setVoiceSecs] = useState(0);
  // 最後一次用哪個管道送出，只影響說明文案。
  const [lastChannel, setLastChannel] = useState<"sms" | "voice">("sms");
  const [verified, setVerified] = useState(!!alreadyVerified);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const sentPhone = useRef<string | null>(null);
  const triedCode = useRef<string | null>(null);

  const sendOtp = async (targetPhone: string, manual = false, channel: "sms" | "voice" = "sms") => {
    if (!targetPhone) return;
    if (!lineAccessToken) {
      liff.login();
      return;
    }
    setError("");
    try {
      const res = await api.post<{ data?: { resendAvailableInSeconds?: number } }>(
        "/app-api/line/send-phone-otp",
        { accessToken: lineAccessToken, phone: targetPhone, channel },
        { isAuth: false },
      );
      setLastChannel(channel);
      // 只有簡訊記進 otpSent：那筆記錄是購物車預寄與本頁之間「別重寄付費簡訊」的依據
      //（recentlySentTo 也讀它）。語音寫進去會讓簡訊的自動發送被誤判為已寄過而跳過。
      if (channel === "sms") setOtpSent(targetPhone.replace(/\D/g, ""), Date.now());
      // 冷卻秒數以後端回傳為準（它才知道自己的 sent_at）；沒回才退回本地常數。
      // 自己算會比後端短 → 使用者按「重新傳送」又吃 429，看起來像按了沒反應。
      const cooldown = res.data?.data?.resendAvailableInSeconds || OTP_RESEND_SECS;
      if (channel === "voice") setVoiceSecs(cooldown);
      else setSecs(cooldown);
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { message?: unknown } } } | undefined;
      const raw = err?.response?.data?.message;
      const msg = (Array.isArray(raw) ? raw.join(" ") : typeof raw === "string" ? raw : "").toLowerCase();
      // 429「冷卻中」只代表『這個 lineUserId 最近寄過一封』——後端冷卻是 per lineUserId，
      // 並沒有告訴我們那封寄到哪支號碼。
      // 只有在我們自己記錄了「剛剛寄給同一支號碼」時，才能安心當成良性重複
      //（購物車預寄 + 結算頁再送一次，SMS 實際只會有一封，使用者手上確實有碼）。
      // 號碼對不上就必須照實報錯：那封碼寄到別支去了，若在此靜默顯示輸入框，
      // 使用者會盯著一支永遠收不到碼的手機等，畫面上卻沒有任何線索。
      const left = recentlySentTo(targetPhone);
      // 只有「自動發送」（掛載時）才可以靜默：那是預寄與結算頁的重複請求。
      // 使用者親手按「重新傳送」卻被冷卻擋下時必須看到訊息，否則是按了沒反應。
      if (err?.response?.status === 429 && !msg.includes("daily") && left > 0 && !manual && channel === "sms") {
        setSecs(left);
        setError("");
        return;
      }
      setError(getSendOtpErrorMessage(e));
    }
  };

  /** 已通過驗證的門號：驗證只對這支號碼成立，換號就得重來。
   *  null = 還不知道是哪一支（結算頁的 contactPhone 是掛載後才補進來的，見下方 effect）。 */
  const verifiedPhone = useRef<string | null>(alreadyVerified && phone ? phone : null);
  /** 這次驗證是不是「在本頁完成」的（相對於上一關帶進來的 alreadyVerified）。 */
  const verifiedHere = useRef(false);

  // 門號換掉 → 作廢先前的驗證（元件自己守，不依賴呼叫端記得重置 store）。
  // 少了這段：註冊時驗過 A 進來（alreadyVerified=true）→ 改成 B → verified 仍為 true，
  // 下面的發送 effect 被 verified 短路而不送簡訊，等於 B 沒驗證就過關。
  useEffect(() => {
    if (!phone) return;
    // 「從沒有號碼變成有號碼」不是換號：結算頁掛載時 contactPhone 可能還是空字串，
    // 稍後才由 userData.phone 補上。此時把它認定成被驗證的那一支，不可作廢
    //（誤判的後果就是：註冊時明明驗過，結算頁又要重驗一次）。
    if (verifiedPhone.current === null) {
      if (verified) verifiedPhone.current = phone;
      return;
    }
    if (verifiedPhone.current === phone) return;
    verifiedPhone.current = null;
    sentPhone.current = null;
    verifiedHere.current = false;
    setVerified(false);
    setOtpVerified(false);
    setPhoneVerifiedTicket("");
    setError("");
  }, [phone, verified, setOtpVerified, setPhoneVerifiedTicket]);

  // 掛載/門號變動時發送簡訊 OTP（同一門號只自動送一次，重送交給下方倒數按鈕）
  useEffect(() => {
    if (!phone || verified || sentPhone.current === phone) return;
    sentPhone.current = phone;
    // 購物車已預寄且仍在有效倒數內 → 不重寄（真簡訊要錢，也避免使用者收到兩封）
    const left = recentlySentTo(phone);
    if (left > 0) {
      setSecs(left);
      return;
    }
    void sendOtp(phone);
  }, [phone, verified]);

  // 進頁自動 focus 第一個空格（dev UX：進頁即可直接輸入驗證碼）
  useEffect(() => {
    const t = setTimeout(() => {
      const firstEmpty = code.findIndex((c) => !c);
      inputs.current[firstEmpty === -1 ? 0 : firstEmpty]?.focus();
    }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (secs <= 0 || verified) return;
    const t = setInterval(() => setSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [secs, verified]);

  // 語音冷卻倒數（與簡訊各自獨立）
  useEffect(() => {
    if (voiceSecs <= 0 || verified) return;
    const t = setInterval(() => setVoiceSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [voiceSecs, verified]);

  // 填滿 5 碼 → 向後端驗證；碼被改動則退回未驗證，同一組碼失敗過不重打
  useEffect(() => {
    const full = code.join("").length === 5;
    if (!full) {
      // 只有「在這一頁驗證成功」的才會因為改動輸入碼而退回未驗證。
      // 上一關（onboarding OTP）帶進來的 alreadyVerified 不適用——進頁時輸入框本來就是空的，
      // 若一併退回，等於 alreadyVerified 永遠失效：掛載後立刻 verified=false → 重發簡訊。
      if (verified && verifiedHere.current) setVerified(false);
      return;
    }
    const otpValue = code.join("");
    if (verified || verifying || triedCode.current === otpValue) return;
    if (!lineAccessToken) {
      liff.login();
      return;
    }
    triedCode.current = otpValue;
    setVerifying(true);
    setError("");
    handleRequest({
      asyncRequest: () => api.post("/app-api/line/verify-phone-otp", { accessToken: lineAccessToken, phone, otpCode: otpValue }, { isAuth: false }),
      successCallback: (res: AxiosResponse) => {
        setPhoneVerifiedTicket(res?.data?.data?.phoneVerifiedTicket || "");
        setOtpVerified(true);
        verifiedHere.current = true;
        setVerified(true);
      },
      errorCallback: () => {
        setError("驗證碼錯誤，請重新輸入");
        setCode(["", "", "", "", ""]);
        // 清空後把游標移回第一格，使用者可直接重打（autoFocus 只在首次掛載生效，不會再觸發）。
        // 放 setTimeout 讓清空後的重繪先完成，否則 focus 可能被隨後的 re-render 蓋掉。
        setTimeout(() => inputs.current[0]?.focus(), 0);
      },
      finalCallback: () => setVerifying(false),
    });
  }, [code, verified, verifying, phone, lineAccessToken]);

  const masked = phone ? phone.replace(/(\d{4})\d{3}(\d{3})/, "$1＊＊＊$2") : "";

  /** 從第 start 格起依序填入多個數字（簡訊自動填入/貼上整組驗證碼會一次給整串） */
  const fillFrom = (start: number, raw: string) => {
    const digits = raw.replace(/\D/g, "").split("");
    if (digits.length === 0) return;
    const n = [...code];
    digits.slice(0, code.length - start).forEach((d, k) => {
      n[start + k] = d;
    });
    setCode(n);
    const lastFilled = Math.min(start + digits.length, code.length) - 1;
    inputs.current[Math.min(lastFilled + 1, code.length - 1)]?.focus();
  };
  const setDigit = (i: number, v: string) => {
    if (verifying) return;
    // 自動填入/貼上：整組驗證碼會塞進單一格 → 分配到各格（不可只取第一碼）
    if (v.replace(/\D/g, "").length > 1) {
      fillFrom(i, v);
      return;
    }
    if (!/^\d?$/.test(v)) return;
    const n = [...code];
    n[i] = v;
    setCode(n);
    if (v && i < code.length - 1) inputs.current[i + 1]?.focus();
  };
  const onKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[i] && i > 0) inputs.current[i - 1]?.focus();
  };

  return (
    <>
      <SectionLabel>簡訊驗證</SectionLabel>
      <Card>
        {verified ? (
          <XStack ai="center" gap={12} py={8}>
            <YStack className="kiosk-ringpop" ai="center" jc="center" width={34} height={34} borderRadius={17} backgroundColor="#10B981">
              <Text fontSize={18} fontWeight="900" color="#fff">
                ✓
              </Text>
            </YStack>
            <YStack>
              <Text fontSize={14.5} fontWeight="800" color="#15803D">
                驗證成功
              </Text>
              <Text fontSize={12.5} color={kiosk.grey} mt={1}>
                已完成 {masked || "門號"} 的身分驗證
              </Text>
            </YStack>
          </XStack>
        ) : (
          <YStack py={4} gap={14}>
            <Text fontSize={13} color={kiosk.grey}>
              {lastChannel === "voice"
                ? `首次下單需驗證身分，${VOICE_OTP_CALLER_ID} 將撥打電話至 ${masked || "您的門號"}，會唸兩次驗證碼`
                : `首次下單需驗證身分，驗證碼已傳送至 ${masked || "您的門號"}`}
            </Text>

            <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
              {code.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    inputs.current[i] = el;
                  }}
                  value={d}
                  inputMode="numeric"
                  // 簡訊驗證碼自動填入：只掛第一格（iOS/Android 會把「整組」塞進這一格，
                  // 再由 setDigit/fillFrom 分配到各格）。不可設 maxLength=1——會把整組截成單碼。
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  name={i === 0 ? "one-time-code" : undefined}
                  disabled={verifying}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onPaste={(e) => {
                    e.preventDefault();
                    fillFrom(i, e.clipboardData.getData("text"));
                  }}
                  onKeyDown={(e) => onKey(i, e)}
                  style={{
                    width: 46,
                    height: 54,
                    textAlign: "center",
                    fontSize: 22,
                    fontWeight: 700,
                    color: kiosk.ink,
                    border: `1.5px solid ${d ? kiosk.blue : kiosk.line}`,
                    borderRadius: 12,
                    outline: "none",
                    background: d ? "#EFF6FF" : "#fff",
                    boxSizing: "border-box",
                    opacity: verifying ? 0.6 : 1,
                  }}
                />
              ))}
            </div>

            {/* 錯誤訊息與「倒數／重新傳送」分開兩行。
                原本三者共用同一位置（error ? ... : secs > 0 ? ... : 重送），
                只要出現過任何錯誤（輸錯驗證碼最常見）error 就一直有值，
                重送鍵永遠到不了 → 使用者卡死，只能重整頁面。 */}
            {!!error && (
              <Text fontSize={13} color={kiosk.danger} fontWeight="600">
                {error}
              </Text>
            )}
            {verifying ? (
              <Text fontSize={13} color={kiosk.grey}>
                驗證中…
              </Text>
            ) : (
              <YStack gap={6}>
                {secs > 0 ? (
                  <Text fontSize={13} color={kiosk.grey}>
                    {secs} 秒後可重新傳送
                  </Text>
                ) : (
                  <Text fontSize={13} color={kiosk.blue} fontWeight="600" cursor="pointer" onPress={() => sendOtp(phone, true)}>
                    重新傳送驗證碼
                  </Text>
                )}
                {/* 收不到簡訊的救援管道：改由系統打電話唸驗證碼（市話也收得到）。
                    與「重新傳送」同為藍字，但不加粗、字級小一級，並以上方細線分開——
                    它是備援出口，不該和主要動作看起來像兩個同等選項。 */}
                <XStack height={1} backgroundColor={kiosk.line} width="55%" my={2} />
                {voiceSecs > 0 ? (
                  <Text fontSize={12.5} color="#CBD5E1">
                    語音驗證 {voiceSecs} 秒後可再試
                  </Text>
                ) : (
                  <Text fontSize={12.5} color={kiosk.blue} cursor="pointer" onPress={() => sendOtp(phone, true, "voice")}>
                    改用語音驗證
                  </Text>
                )}
              </YStack>
            )}
          </YStack>
        )}
      </Card>
    </>
  );
};

export default OtpCard;
